// Mirror of lud-backend/utils/menuHealth.js. The backend applies these rules
// to every menu in the list response (`counts`, used for the restaurant
// overview); this copy applies them to the fully loaded selected menu and to
// the item filters, so a card count and the items it filters always match.
// menuHealth.test.js pins it to the backend's vectors.

import { DEFAULT_PRICING_NAME } from "./menuPricing";

const blank = (value) => !String(value ?? "").trim();

export function isPricingEntryValid(entry) {
  const price = Number(entry?.price);
  if (blank(entry?.name)) return false;
  if (entry?.price === "" || entry?.price === null || entry?.price === undefined || !Number.isFinite(price) || price < 0) return false;
  const discount = entry?.discount;
  if (discount === null || discount === undefined) return true;
  if (typeof discount !== "object" || !["amount", "percentage"].includes(discount.type)) return false;
  const value = Number(discount.value);
  if (!Number.isFinite(value) || value < 0) return false;
  if (discount.type === "amount") return value <= price;
  return value <= 100;
}

export function itemHealth(item, assignedBranches = new Set()) {
  const pricing = Array.isArray(item?.pricing) ? item.pricing : [];
  const hidden = Boolean(item?.visibility?.hidden);
  const noPricing = pricing.length === 0;
  const noBasePrice = !noPricing && !pricing.some((entry) => entry?.name === DEFAULT_PRICING_NAME);
  const invalidPricing = !noPricing && !noBasePrice && pricing.some((entry) => !isPricingEntryValid(entry));
  const missingArabicName = blank(item?.name?.ar);
  const missingEnglishName = blank(item?.name?.en);
  const pricingIssue = noPricing || noBasePrice || invalidPricing;
  return {
    hidden,
    hiddenInSomeBranches: !hidden && (item?.visibility?.hiddenBranches || []).some((entry) => assignedBranches.has(String(entry?.branch))),
    noImage: blank(item?.image),
    noPricing,
    noBasePrice,
    invalidPricing,
    pricingIssue,
    missingArabicName,
    missingEnglishName,
    missingDescription: blank(item?.description?.ar) && blank(item?.description?.en),
    needsAttention: pricingIssue || missingArabicName || missingEnglishName,
  };
}

export function menuHealthCounts(menu) {
  const sections = (menu?.sections || []).filter((s) => !s.archivedAt);
  const items = sections.flatMap((s) => (s.items || []).filter((i) => !i.archivedAt));
  const assigned = new Set((menu?.branches || []).map(String));
  const flags = items.map((item) => itemHealth(item, assigned));
  const count = (key) => flags.filter((f) => f[key]).length;
  return {
    sections: sections.length,
    items: items.filter((i) => !i.isBox).length,
    boxes: items.filter((i) => i.isBox).length,
    hiddenItems: count("hidden"),
    itemsHiddenInSomeBranches: count("hiddenInSomeBranches"),
    itemsWithoutImage: count("noImage"),
    emptySections: sections.filter((s) => !(s.items || []).some((i) => !i.archivedAt)).length,
    itemsWithoutPricing: count("noPricing"),
    itemsWithoutBasePrice: count("noBasePrice"),
    itemsWithInvalidPricing: count("invalidPricing"),
    itemsWithPricingIssues: count("pricingIssue"),
    itemsMissingArabicName: count("missingArabicName"),
    itemsMissingEnglishName: count("missingEnglishName"),
    itemsMissingName: flags.filter((f) => f.missingArabicName || f.missingEnglishName).length,
    itemsWithoutDescription: count("missingDescription"),
    itemsNeedingAttention: count("needsAttention"),
  };
}

// ------------------------------------------------------------ item filters
// Values of the Menu page's item filter. `hidden`/`visible` keep their
// original meaning (a selected branch also counts as hiding the item).
export const ITEM_FILTERS = ["all", "visible", "hidden", "noImage", "attention", "pricing", "names", "noDescription"];

export function matchesItemFilter(filter, item, { assignedBranches, selectedBranchId = "" } = {}) {
  if (!filter || filter === "all") return true;
  if (filter === "hidden" || filter === "visible") {
    const hidden = item.visibility?.hidden || (selectedBranchId && (item.visibility?.hiddenBranches || []).some((h) => String(h.branch) === selectedBranchId));
    return filter === "hidden" ? Boolean(hidden) : !hidden;
  }
  const flags = itemHealth(item, assignedBranches);
  if (filter === "noImage") return flags.noImage;
  if (filter === "attention") return flags.needsAttention;
  if (filter === "pricing") return flags.pricingIssue;
  if (filter === "names") return flags.missingArabicName || flags.missingEnglishName;
  if (filter === "noDescription") return flags.missingDescription;
  return true;
}

// Per-menu count behind each item filter (restaurant cards use it to jump to
// a menu that actually has matching items).
export const FILTER_COUNT_KEY = { hidden: "hiddenItems", noImage: "itemsWithoutImage", attention: "itemsNeedingAttention", pricing: "itemsWithPricingIssues" };

// ------------------------------------------------------------ menu issues
// Menu-level problems, from a list summary. Archived menus are out of use and
// never reported.
export function menuIssues(summary) {
  if (!summary || summary.lifecycle === "archived") return [];
  const c = summary.counts || {};
  const issues = [];
  if ((c.items || 0) + (c.boxes || 0) === 0) issues.push("noItems");
  if (c.emptySections > 0) issues.push("emptySections");
  if (c.itemsNeedingAttention > 0) issues.push("itemIssues");
  if (summary.lifecycle === "active" && !(summary.branches || []).length) issues.push("noBranches");
  return issues;
}

export function matchesMenuFilter(filter, summary) {
  if (!filter) return true;
  if (filter === "issues") return menuIssues(summary).length > 0;
  return summary.lifecycle === filter;
}

// Restaurant totals from list summaries (no extra requests). Content and
// quality totals cover menus in use (active + draft); archived menus only
// count towards the menu totals.
export function summarizeRestaurant(menus = []) {
  const lifecycle = { active: 0, draft: 0, archived: 0 };
  const totals = { sections: 0, items: 0, boxes: 0, hiddenItems: 0, itemsHiddenInSomeBranches: 0, itemsWithoutImage: 0, itemsWithoutDescription: 0, itemsWithPricingIssues: 0, itemsMissingArabicName: 0, itemsMissingEnglishName: 0, itemsNeedingAttention: 0 };
  const issues = { menus: 0, itemIssues: 0, noBranches: 0, noItems: 0, emptySections: 0 };
  for (const menu of menus) {
    if (lifecycle[menu.lifecycle] !== undefined) lifecycle[menu.lifecycle] += 1;
    if (menu.lifecycle === "archived") continue;
    for (const key of Object.keys(totals)) totals[key] += Number(menu.counts?.[key]) || 0;
    const found = menuIssues(menu);
    if (found.length) issues.menus += 1;
    for (const key of found) issues[key] += 1;
  }
  return { menus: menus.length, lifecycle, inUse: lifecycle.active + lifecycle.draft, totals, issues };
}

// Additions linked from the selected menu's items. A link is broken when the
// addition no longer exists or was archived/deactivated after linking.
export function additionUsage(menu) {
  const used = new Map();
  let items = 0;
  let broken = 0;
  for (const section of menu?.sections || []) {
    for (const item of section.items || []) {
      const groups = item.additionGroups || [];
      if (groups.length) items += 1;
      for (const group of groups) {
        const addition = typeof group.addition === "object" ? group.addition : { _id: group.addition };
        used.set(String(addition?._id), addition);
      }
    }
  }
  for (const addition of used.values()) if (addition?.missing || addition?.archivedAt || addition?.isActive === false) broken += 1;
  return { additions: used.size, items, broken };
}
